<template>
  <div>
    <div
      :class="$style.searchContainer"
    >
      <form
        :class="getSearchFormClass"
        @submit.prevent=""
      >
        <i
          ref="searchIcon"
          :class="[
            $style.searchIcon,
            'jc-actions-search',
            {[$style.disabledSearchIcon]: disableSearchBar},
          ]"
        />
        <input
          ref="searchInput"
          v-on-clickaway="hideList"
          :class="[
            $style.searchInput,
            {[$style.disabledInput]: disableSearchBar, },
          ]"
          :disabled="disableSearchBar"
          :placeholder="placeholderText"
          :style="inputStyle"
          type="text"
          :value="inputText"
          @click="handleSearchClick"
          @input="handleSearchInput"
          @keyup="handleSearchKeyup"
          @keyup.esc="handleSearchClearButtonClick"
        >
        <transition
          :enterActiveClass="$style.searchClearButtonEnterActive"
          :enterClass="$style.searchClearButtonEnter"
          :leaveActiveClass="$style.searchClearButtonLeaveActive"
          :leaveToClass="$style.searchClearButtonLeaveTo"
        >
          <button
            v-if="searchTerm"
            ref="searchClearButton"
            :class="$style.searchClearButton"
            :style="searchClearButtonStyle"
            type="button"
            @click.prevent="handleSearchClearButtonClick"
          >
            <i
              class="jc-actions-x"
              title="Clear Search"
            />
          </button>
        </transition>
        <AppliedFilters
          v-if="useDropDownFilterComponent !== ''"
          :searchFetch="searchFetch"
          :setInputPadding="setInputPadding"
          :storeModule="storeModule"
        />
        <span
          ref="searchInputHelper"
          :class="$style.searchInputHelper"
        >
          {{ searchTerm }}
        </span>
        <component
          :is="useSearchContextFilterComponent"
          v-if="useSearchContextFilterComponent"
          ref="filterComponent"
          :class="$style.searchResults"
          :setSearchInputValue="setSearchInputValue"
          :showList="showList"
          :storeModule="storeModule"
        />
      </form>
      <component
        :is="useDropDownFilterComponent"
        v-if="useDropDownFilterComponent"
        :class="$style.filterContainer"
        :setLoadingOverlay="setLoadingOverlay"
        :storeModule="storeModule"
      />
    </div>
  </div>
</template>

<script>
import { FilterComponentConsts } from 'client/util/Constants';
import { mixin as clickaway } from 'vue-clickaway';
import { debounce, extend } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import AppliedFilters from 'client/components/ItemTable/CollectionFilterComponents/AppliedFilters.vue';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import PolicySystemStatusSearchFilter from 'client/components/ItemTable/SearchFilterComponents/PolicySystemStatusSearchFilter.vue';
import SystemSearchFilter from 'client/components/ItemTable/SearchFilterComponents/SystemSearchFilter.vue';
import SystemUserSearchFilter from 'client/components/ItemTable/SearchFilterComponents/SystemUserSearchFilter.vue';
import axios from 'axios';

import AppleSoftwareManagementDropDownFilter from 'client/components/ItemTable/CollectionFilterComponents/AppleSoftwareManagementDropDownFilter';
import ApplicationsDropDownFilter from 'client/components/ItemTable/CollectionFilterComponents/ApplicationsDropDownFilter';
import AuthNPoliciesDropDownFilter from './CollectionFilterComponents/AuthNPoliciesDropDownFilter.vue';
import BrowserPolicyDropDownFilter from './CollectionFilterComponents/BrowserPolicyDropDownFilter.vue';
import CommandTemplatesDropDownFilter from './CollectionFilterComponents/CommandTemplatesDropDownFilter.vue';
import CommandsDropDownFilter from './CollectionFilterComponents/CommandsDropDownFilter.vue';
import GSuiteImportSelectionDropDownFilter from './CollectionFilterComponents/GSuiteImportSelectionDropDownFilter.vue';
import GroupAsideSystemDropDownFilter from './CollectionFilterComponents/GroupAsideSystemDropDownFilter.vue';
import GroupDropDownFilter from './CollectionFilterComponents/GroupDropDownFilter.vue';
import O365ImportSelectionDropDownFilter from './CollectionFilterComponents/O365ImportSelectionDropDownFilter.vue';
import OsPolicyDropDownFilter from './CollectionFilterComponents/OsPolicyDropDownFilter.vue';
import PolicyCategoryDropDownFilter from './CollectionFilterComponents/PolicyCategoryDropDownFilter.vue';
import PolicyDropDownFilter from './CollectionFilterComponents/PolicyDropDownFilter.vue';
import PolicySystemDropDownFilter from './CollectionFilterComponents/PolicySystemDropDownFilter.vue';
import PolicySystemStatusDropDownFilter from './CollectionFilterComponents/PolicySystemStatusDropDownFilter.vue';
import ScimUsersImportSelectionDropDownFilter from './CollectionFilterComponents/ScimUsersImportSelectionDropDownFilter.vue';
import SystemDropDownFilter from './CollectionFilterComponents/SystemDropDownFilter.vue';
import SystemUserDropDownFilter from './CollectionFilterComponents/SystemUserDropDownFilter.vue';

export default {
  name: 'ItemTableSearchBar',

  components: {
    AppleSoftwareManagementDropDownFilter,
    AppliedFilters,
    ApplicationsDropDownFilter,
    BrowserPolicyDropDownFilter,
    CommandsDropDownFilter,
    CommandTemplatesDropDownFilter,
    AuthNPoliciesDropDownFilter,
    GSuiteImportSelectionDropDownFilter,
    GroupAsideSystemDropDownFilter,
    GroupDropDownFilter,
    O365ImportSelectionDropDownFilter,
    OsPolicyDropDownFilter,
    PolicyCategoryDropDownFilter,
    PolicyDropDownFilter,
    PolicySystemDropDownFilter,
    PolicySystemStatusDropDownFilter,
    PolicySystemStatusSearchFilter,
    ScimUsersImportSelectionDropDownFilter,
    SystemDropDownFilter,
    SystemSearchFilter,
    SystemUserDropDownFilter,
    SystemUserSearchFilter,
  },

  mixins: [
    ItemTablePropsMixin,
    clickaway,
  ],

  props: {
    disableSearchBar: {
      default: false,
      type: Boolean,
    },
    placeholderText: {
      type: String,
      required: true,
    },
    preventSearchReset: {
      default: false,
      type: Boolean,
    },
    selectionItemText: {
      type: Object,
      required: true,
    },
    setLoadingOverlay: {
      type: Function,
      required: true,
    },
    storeModule: {
      type: String,
      required: true,
    },
    useDropDownFilterComponent: {
      type: String,
      default: '',
    },
    useSearchContextFilterComponent: {
      type: String,
      required: false,
      default: '',
    },
  },

  data: () => ({
    inputTextLeft: 0,
    searchClearButtonLeft: 80,
    showList: false,
    customInputText: '',
  }),

  computed: {
    getSearchFormClass() {
      const className = this.useModernSearchStyles ? 'modernSearchStyles' : 'searchForm';
      return this.$style[className];
    },

    inputStyle: {
      cache: false,
      get() {
        const style = { textIndent: `${this.inputTextLeft + 17}px` };
        if (this.disableSearchBar) style.cursor = 'not-allowed';
        return style;
      },
    },

    inputText() {
      return this.customInputText === '' ? this.searchTerm : this.customInputText;
    },

    searchClearButtonStyle: {
      // needed in order to call the computed function on state change
      cache: false,
      get() {
        // Override the style for now if this is called for an MSP
        // TODO: Lindsey will be tweaking these settings
        if (this.useModernSearchStyles) {
          return { 'padding-right': '25px !important' };
        }
        return { left: `${this.searchClearButtonLeft}px` };
      },
    },
  },

  beforeCreate() {
    extend(
      this.$options.computed,
      mapGetters(this.$options.propsData.storeModule, {
        searchTerm: 'getSearchTerm',
        getFilters: 'getFilters',
        getSearchField: 'getSearchField',
      }),
    );

    extend(
      this.$options.methods,
      mapActions(this.$options.propsData.storeModule, [
        'deleteFilter',
        'fetchCollection',
        'setSearchTerm',
      ]),
    );
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
    ]),

    debounceSearch: debounce(function handleDebounce() {
      this.searchFetch();
    }, 250),

    handleSearchClearButtonClick() {
      this.customInputText = '';
      this.setSearchTerm({ searchTerm: '', preventReset: this.preventSearchReset });
      this.searchFetch();
    },

    handleSearchClick() {
      if (this.useSearchContextFilterComponent) {
        this.showList = true;
      }
    },

    handleSearchInput(event) {
      // set the search term
      const { value } = event.target;

      if (event.inputType && event.inputType === 'deleteContentBackward') {
        this.customInputText = '';
      }

      this.setSearchTerm({ searchTerm: value, preventReset: this.preventSearchReset });

      const searchObj = this.getSearchField(value);
      // figure out if there is a search field present on the search term
      if (searchObj.field) {
        // run a search if there is at least 1 character not including the search field
        if (searchObj.value.length >= 1 || searchObj.value.length === 0) {
          this.setClearButtonPosition(value);
          this.debounceSearch();
        }
        // run a search if there are at least 2 characters
      } else if (value.length >= 2 || value.length === 0) {
        this.setClearButtonPosition(value);
        this.debounceSearch();
      }

      this.showList = false;
    },

    handleSearchKeyup() {
      if (this.searchTerm.length === 0
        && this.useSearchContextFilterComponent
          === FilterComponentConsts.PolicySystemStatusSearchFilter) {
        this.getFilters.forEach(async (filter) => {
          await this.deleteFilter({ filterName: filter.name });
        });
        this.handleSearchClearButtonClick();
      }
    },

    hideList() {
      this.showList = false;
    },

    searchFetch() {
      this.setLoadingOverlay(true);
      this.fetchCollection()
        .then(() => {
          this.setLoadingOverlay(false);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return;
          }

          this.setLoadingOverlay(false);

          this.triggerErrorNotification({
            title: `Unable to search ${this.selectionItemText.plural}!`,
            message: error ? error.toString() : undefined,
          });
        });
    },

    /**
     * Set the inner text of the search input helper and then use its length
     * plus 80px as the left offset of the clear so that the clear button does
     * not overlap the search input text.
     * @todo For now, if this is an MSP, don't have the 'x' shift position
     */
    setClearButtonPosition(text) {
      if (!this.useModernSearchStyles) {
        const { searchInput, searchInputHelper } = this.$refs;
        searchInputHelper.innerText = text;

        const { width } = searchInputHelper.getBoundingClientRect();
        const inputWidth = searchInput.getBoundingClientRect().width;

        let newLeft = parseInt(width, 10) + this.inputTextLeft + 60;
        if (newLeft > inputWidth - 30) newLeft = inputWidth - 30;
        this.searchClearButtonLeft = newLeft;
      }
    },

    // Push start of text input right of filter value pills
    setInputPadding(clientWidth) {
      const { searchInputHelper } = this.$refs;

      if (searchInputHelper) {
        const { width } = searchInputHelper.getBoundingClientRect();

        this.inputTextLeft = clientWidth;
        this.searchClearButtonLeft = width + clientWidth + 80;
      }
    },

    setSearchInputValue(item, customSearch) {
      let name = item;

      if (customSearch) {
        name = `${item.searchBy}: ${item.item.id}`;
        this.customInputText = `${item.inputText}: ${item.item.displayName}`;
      }

      // Passing empty string to setSearchTerm removes any previous filters
      this.setSearchTerm({ searchTerm: '', preventReset: true });
      this.setSearchTerm({ searchTerm: name, preventReset: true });
      this.setClearButtonPosition(customSearch
        ? `${item.inputText}: ${item.item.displayName}`
        : name);

      this.$refs.searchInput.focus();

      if (customSearch) {
        this.searchFetch();
      }
    },
  },
};
</script>

<style scoped module>
@value (jcSecondary, jcGrey200, jcGrey400) from '~css/brand-colors.css';

.searchContainer {
  display: flex;
}

.modernSearchStyles {
  border-radius: 1.5px;
  margin: 0 0 15px 64px;
  position: relative;
  width: 25%;
}

.searchForm {
  display: flex;
  flex: 1;
  position: relative;
}

.searchIcon {
  color: var(--jcPrimary);
  font-size: 1.46rem;
  line-height: 35px;
  margin-left: 8px;
  position: absolute;
}

.searchInput {
  border-radius: 2px;
  color: jcSecondary;
  display: inline-block;
  font-size: 1.1rem;
  font-weight: 600;
  height: 38px;
  padding: 6px 38px 6px 18px;
  width: 100%;
}

.searchInput,
.searchInput:focus {
  background-color: white;
  border: 2px solid jcGrey200;
  outline: none;
}

.searchInput::placeholder {
  color: jcGrey400;
  font-weight: 600;
  opacity: 0.7;
}

.disabledInput {
  background-color: jcGrey200;
}

.disabledSearchIcon {
  color: jcGrey400;
}

/* Use of !important to override overly specific external styles */
.searchClearButton {
  background-color: inherit !important;
  border: 0;
  border-radius: 0 !important;
  color: #b3b2b1 !important;
  font-size: 1.3rem;
  height: 20px;
  line-height: 20px;
  margin: 0 !important;
  padding: 0 !important;
  position: absolute;
  top: 10px;
  transition: left 0.25s linear;
  width: 20px;
}

.searchClearButton:focus,
.searchClearButton:hover {
  box-shadow: none;
  outline: 0 none;
}

.modernSearchStyles .searchClearButton {
  right: 10px;
}

.searchClearButtonEnterActive {
  transition: opacity 250ms ease-in;
}

.searchClearButtonLeaveActive {
  transition: opacity 500ms ease-out;
}

.searchClearButtonEnter,
.searchClearButtonLeaveTo {
  opacity: 0;
}

.searchResults {
  left: 0;
  position: absolute;
  top: calc(100% + 2px);
  width: 100%;
}

.searchInputHelper {
  font-size: 1.08rem;
  font-weight: 600;
  height: auto;
  left: -10000px;
  position: absolute;
  visibility: hidden;
  white-space: nowrap;
  width: auto;
}

.filterContainer {
  border-left: 0;
}
</style>
